import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Play, 
  Trophy, 
  Clock, 
  Target,
  Zap,
  Brain,
  Eye,
  Calculator,
  Receipt,
  Layers,
  Bird,
  Scale,
  Crosshair,
  Timer,
  TrendingUp,
  Wind,
  Flame,
  Award,
  Gamepad2,
  Users,
  CircleDollarSign,
  Coins,
  Crown
} from 'lucide-react';

// Import individual game components
import TapChallenge from '../components/games/TapChallenge';
import MemoryPuzzle from '../components/games/MemoryPuzzle';
import LightningQuiz from '../components/games/LightningQuiz';
import ReactionTest from '../components/games/ReactionTest';
import FlappyTap from '../components/games/FlappyTap';
import BallBalance from '../components/games/BallBalance';
import TargetHitAccuracy from '../components/games/TargetHitAccuracy';
import PerfectTimingTap from '../components/games/PerfectTimingTap';
import BallJump from '../components/games/BallJump';
import BottleFlip from '../components/games/BottleFlip';
import TapFlash from '../components/games/TapFlash';
import Leaderboard from '../components/Leaderboard';
import PlayerWallet from '../components/PlayerWallet';
import UPIPaymentModal from '../components/UPIPaymentModal';
import playerManager from '../utils/playerManager';
import { toast } from 'sonner';

const PlayGames = () => {
  const [selectedGame, setSelectedGame] = useState(null);
  const [playerStats, setPlayerStats] = useState({
    gamesPlayed: 0,
    totalScore: 0,
    bestGame: null
  });
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [gameToPlay, setGameToPlay] = useState(null);
  const [activeGames, setActiveGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [competitionGames, setCompetitionGames] = useState([]);
  const [practiceGames, setPracticeGames] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const [competitionTimers, setCompetitionTimers] = useState({});
  const [topPrize, setTopPrize] = useState('Amazing Prizes');

  const games = [
    {
      id: 'tap-challenge',
      title: 'Tap Challenge',
      description: 'Click targets as fast as you can!',
      duration: 60,
      difficulty: 'Medium',
      icon: Target,
      color: 'blue',
      component: TapChallenge,
      playersToday: 1563,
      timeLeft: "3h 33m",
      rules: 'Click the targets that appear on screen. Each target gives points based on speed and accuracy.'
    },
    {
      id: 'memory-puzzle',
      title: 'Memory Master',
      description: 'Remember the sequence and repeat it',
      duration: 90,
      difficulty: 'Hard',
      icon: Brain,
      color: 'purple',
      component: MemoryPuzzle,
      playersToday: 756,
      timeLeft: "4h 18m",
      rules: 'Watch the sequence of colors/numbers and repeat it exactly. Sequences get longer each round.'
    },
    {
      id: 'lightning-quiz',
      title: 'Lightning Quiz',
      description: 'Answer questions at lightning speed',
      duration: 120,
      difficulty: 'Easy',
      icon: Zap,
      color: 'yellow',
      component: LightningQuiz,
      playersToday: 1247,
      timeLeft: "2h 45m",
      rules: 'Answer as many questions correctly as possible. Speed and accuracy both matter.'
    },
    {
      id: 'reaction-test',
      title: 'Reaction Master',
      description: 'Test your reflexes and reaction time',
      duration: 45,
      difficulty: 'Medium',
      icon: Eye,
      color: 'green',
      component: ReactionTest,
      playersToday: 923,
      timeLeft: "6h 52m",
      rules: 'Click when the screen changes color. Faster reactions = higher scores.'
    },
    // Perfect Stack game removed
    {
      id: 'flappy-tap',
      title: 'Flappy Tap Challenge',
      description: 'Tap to fly and avoid obstacles!',
      duration: 60,
      difficulty: 'Hard',
      icon: Bird,
      color: 'blue',
      component: FlappyTap,
      playersToday: 1156,
      timeLeft: "4h 42m",
      rules: 'Tap to keep the bird flying. Navigate through pipes without hitting them. Each pipe passed = 1 point!'
    },
    {
      id: 'ball-balance',
      title: 'Ball Balance',
      description: 'Keep the ball balanced in the center zone!',
      duration: 45,
      difficulty: 'Medium',
      icon: Scale,
      color: 'purple',
      component: BallBalance,
      playersToday: 892,
      timeLeft: "3h 22m",
      rules: 'Tilt the platform using mouse/touch to keep the ball in the green center zone. Score points by staying balanced!'
    },
    {
      id: 'target-hit',
      title: 'Target Hit Accuracy',
      description: 'Hit moving targets with precision!',
      duration: 60,
      difficulty: 'Hard',
      icon: Crosshair,
      color: 'red',
      component: TargetHitAccuracy,
      playersToday: 1043,
      timeLeft: "2h 55m",
      rules: 'Click on moving targets as accurately as possible. Hit the center for maximum points. Build combos for bonus points!'
    },
    {
      id: 'timing-tap',
      title: 'Perfect Timing Tap',
      description: 'Master the art of perfect timing!',
      duration: 60,
      difficulty: 'Medium',
      icon: Timer,
      color: 'blue',
      component: PerfectTimingTap,
      playersToday: 967,
      timeLeft: "5h 8m",
      rules: 'Tap when the moving bar reaches the green zone. Perfect timing gives 100 points. Build streaks to increase speed!'
    },
    {
      id: 'ball-jump',
      title: 'Ball Jump Challenge',
      description: 'Jump from platform to platform!',
      duration: 60,
      difficulty: 'Hard',
      icon: TrendingUp,
      color: 'indigo',
      component: BallJump,
      playersToday: 1234,
      timeLeft: "1h 47m",
      rules: 'Jump on platforms to score points. You have 3 lives. Game speeds up as you score more!'
    },
    {
      id: 'bottle-flip',
      title: 'Bottle Flip Challenge',
      description: 'Flip the bottle and land it perfectly!',
      duration: 0,
      difficulty: 'Medium',
      icon: Wind,
      color: 'cyan',
      component: BottleFlip,
      playersToday: 1521,
      timeLeft: "6h 12m",
      rules: 'Hold to charge power, release to flip the bottle. Land it upright on the platform for points. 15 attempts!'
    },
    {
      id: 'tap-flash',
      title: 'Tap Flash',
      description: 'Fastest finger wins! Ultimate reaction test!',
      duration: 60,
      difficulty: 'Medium',
      icon: Zap,
      color: 'yellow',
      component: TapFlash,
      playersToday: 1892,
      timeLeft: "4h 38m",
      rules: 'Tap the screen as fast as possible when it flashes! Faster reactions = more points. Complete 10 rounds!'
    }
  ];

  // Initialize player on component mount
  useEffect(() => {
    const initPlayer = async () => {
      const player = await playerManager.getOrCreatePlayer();
      setCurrentPlayer(player);
      const stats = playerManager.getPlayerStats();
      if (stats) {
        setPlayerStats(stats);
      }
    };
    initPlayer();
    
    // Fetch active games from backend
    fetchActiveGames();
    
    // Refresh game data every 30 seconds to catch new competitions
    const refreshInterval = setInterval(fetchActiveGames, 30000);
    
    // Also refresh when page becomes visible (important for PWA on mobile)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchActiveGames();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Update competition timers every second
  useEffect(() => {
    const interval = setInterval(() => {
      const newTimers = {};
      gamesData.forEach(game => {
        if (game.competition_mode && game.competition_end) {
          const endTime = new Date(game.competition_end);
          const now = new Date();
          const timeLeft = endTime - now;
          
          if (timeLeft > 0) {
            const minutes = Math.floor(timeLeft / 60000);
            const seconds = Math.floor((timeLeft % 60000) / 1000);
            newTimers[game.game_id] = {
              minutes,
              seconds,
              isActive: true,
              endTime: game.competition_end
            };
          } else {
            newTimers[game.game_id] = {
              minutes: 0,
              seconds: 0,
              isActive: false,
              endTime: game.competition_end
            };
          }
        }
      });
      setCompetitionTimers(newTimers);
    }, 1000);

    return () => clearInterval(interval);
  }, [gamesData]);

  // Handle competition game play (with payment)
  const handlePlayClick = async (game, isCompetition = false) => {
    if (!isCompetition) {
      // Practice mode - play for free
      setSelectedGame(game.id);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      return;
    }

    // Competition mode - require login and payment
    const user = JSON.parse(localStorage.getItem('fokatmain_user') || '{}');
    if (!user.id) {
      toast.error('Please login first to compete');
      setTimeout(() => {
        window.location.href = '/auth';
      }, 1500);
      return;
    }

    // Check wallet balance
    if (user.wallet_balance >= 10) {
      try {
        const token = localStorage.getItem('fokatmain_token');
        const response = await fetch(`${window.location.origin}/api/player/wallet/play`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            amount: 10,
            game_id: game.id
          })
        });

        if (response.ok) {
          toast.success('₹10 deducted from wallet. Good luck!');
          setSelectedGame(game.id);
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        } else {
          toast.error('Payment failed. Please try again.');
        }
      } catch (error) {
        toast.error('Error processing payment');
      }
    } else {
      toast.error('Insufficient wallet balance. Please add money first.');
    }
  };

  const fetchActiveGames = async () => {
    try {
      const response = await fetch(`${window.location.origin}/api/games/active`);
      const data = await response.json();
      
      // Fetch top prize
      try {
        const prizeResponse = await fetch(`${window.location.origin}/api/admin/prizes`);
        const prizeData = await prizeResponse.json();
        const firstPrize = prizeData.prizes?.find(p => p.position === 1);
        if (firstPrize) {
          setTopPrize(firstPrize.prize_name);
        }
      } catch (error) {
        console.log('Prize fetch error:', error);
      }
      
      // Store all games data
      setGamesData(data.active_games);
      
      // Separate competition games and practice games
      const competition = data.active_games.filter(g => 
        g.is_active && g.competition_mode && 
        g.competition_end && new Date(g.competition_end) > new Date()
      );
      const practice = data.active_games.filter(g => g.is_active);
      
      setCompetitionGames(competition.map(g => g.game_id));
      setPracticeGames(practice.map(g => g.game_id));
      
      // For backward compatibility
      const activeGamesList = practice.map(g => g.game_id);
      setActiveGames(activeGamesList);
      setLoadingGames(false);
      
    } catch (error) {
      console.error('Error fetching active games:', error);
      // If error, show all games as practice
      const allGames = ['tap-challenge', 'memory-puzzle', 'lightning-quiz', 'reaction-test', 'flappy-tap', 'ball-balance', 'target-hit', 'timing-tap', 'ball-jump', 'bottle-flip', 'tap-flash'];
      setActiveGames(allGames);
      setPracticeGames(allGames);
      setLoadingGames(false);
    }
  };

  const handleGameComplete = (gameId, score, gameData) => {
    // Use one attempt
    const remainingAttempts = playerManager.useGameAttempt(gameId);
    
    const newStats = {
      gamesPlayed: playerStats.gamesPlayed + 1,
      totalScore: playerStats.totalScore + score,
      bestGame: !playerStats.bestGame || score > playerStats.bestGame.score ? 
        { gameId, score, ...gameData } : playerStats.bestGame
    };
    setPlayerStats(newStats);
    setSelectedGame(null);
    
    // Update player stats from manager
    const updatedStats = playerManager.getPlayerStats();
    if (updatedStats) {
      setPlayerStats(updatedStats);
    }
    
    // Show attempts remaining
    if (remainingAttempts > 0) {
      toast.success(`Game complete! ${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} remaining.`);
    } else {
      toast.info('All attempts used! Pay ₹10 for 3 more attempts.');
    }
  };

  if (selectedGame) {
    const game = games.find(g => g.id === selectedGame);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-center justify-between">
            <Button 
              onClick={() => setSelectedGame(null)}
              className="btn-secondary"
            >
              ← Back to Games
            </Button>
            <div className="flex items-center space-x-4">
              <Button 
                onClick={() => setShowLeaderboard(!showLeaderboard)}
                className={showLeaderboard ? "btn-primary" : "btn-secondary"}
              >
                {showLeaderboard ? "Play Game" : "View Leaderboard"}
              </Button>
              <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30">
                Entry Fee: ₹10
              </Badge>
            </div>
          </div>
          
          {showLeaderboard ? (
            <Leaderboard 
              gameId={selectedGame}
              gameName={game.title}
              playerData={currentPlayer}
              onPlayerRankUpdate={(rankData) => {
                // Handle rank updates if needed
                console.log('Player rank updated:', rankData);
              }}
            />
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                {React.createElement(game.component, {
                  onGameComplete: (score, gameData) => handleGameComplete(selectedGame, score, gameData),
                  gameConfig: game
                })}
              </div>
              <div className="lg:col-span-1">
                <Leaderboard 
                  gameId={selectedGame}
                  gameName={game.title}
                  playerData={currentPlayer}
                  onPlayerRankUpdate={(rankData) => {
                    console.log('Player rank updated:', rankData);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-24 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-purple-900/5 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto animate-fade-in-up">
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mb-6 px-6 py-2">
              Play Now • Win Real Gadgets
            </Badge>
            
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 break-words">
              Play Games
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                {' '}Win Prizes
              </span>
            </h1>
            
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-12 leading-relaxed px-2">
              Join thousands of players daily! Play anytime before deadline. Top 3 win prizes every day!
            </p>
            
            {/* Player Stats */}
            {playerStats.gamesPlayed > 0 && (
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400">{playerStats.gamesPlayed}</div>
                  <div className="text-sm text-gray-400">Games Played</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400">{playerStats.totalScore}</div>
                  <div className="text-sm text-gray-400">Total Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400">
                    {playerStats.bestGame ? playerStats.bestGame.score : 0}
                  </div>
                  <div className="text-sm text-gray-400">Best Score</div>
                </div>
              </div>
            )}
            
            {/* Wallet Access */}
            <div className="text-center mb-8">
              <Button 
                onClick={() => setShowWallet(true)}
                className="btn-secondary px-6 py-3 flex items-center space-x-2 mx-auto"
              >
                <Receipt className="w-5 h-5" />
                <span>View Payment History & Receipts</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Today's Prize Pool */}
      <section className="py-12 bg-gradient-to-b from-gray-900/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 mb-4">
              Today&apos;s Prize Pool
            </Badge>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-2">
              Winner Takes It All! 🏆
            </h2>
            <p className="text-gray-400 text-base">Highest score wins the grand prize</p>
          </div>
          
          <div className="max-w-md mx-auto">
            <Card className="bg-gradient-to-br from-yellow-500/20 via-orange-500/20 to-yellow-500/20 border-2 border-yellow-500/50 p-8 text-center shadow-2xl shadow-yellow-500/20">
              <CardHeader className="pb-6">
                <div className="flex justify-center mb-4">
                  <Crown className="w-16 h-16 text-yellow-400 animate-bounce" />
                </div>
                <div className="text-yellow-400 font-black text-4xl mb-3">#1 Winner</div>
                <div className="text-white font-bold text-3xl mb-2">🏆 {topPrize}</div>
                <div className="text-gray-300 text-base">Grand Prize</div>
              </CardHeader>
              <CardContent>
                <div className="bg-black/30 rounded-lg p-4 mt-4">
                  <p className="text-yellow-300 text-sm font-semibold">
                    ⚡ Play today&apos;s competition game
                  </p>
                  <p className="text-gray-400 text-xs mt-2">
                    Best score in 3 attempts wins!
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Live Competitions Section */}
      {competitionGames.length > 0 && (
        <section className="py-12 bg-gradient-to-b from-transparent via-red-900/10 to-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 mb-4 animate-pulse">
                <Flame className="w-4 h-4 inline mr-1" />
                LIVE NOW
              </Badge>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-4 break-words">
                🏆 Live Competitions
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto px-2">
                Compete for Real Prizes! Pay ₹10 entry • Win Amazing Gadgets
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {games.filter(game => competitionGames.includes(game.id)).map((game, index) => {
                const gameData = gamesData.find(g => g.game_id === game.id) || {};
                const IconComponent = game.icon;
                const endTime = gameData.competition_end ? new Date(gameData.competition_end) : null;
                const timeLeft = endTime ? Math.max(0, Math.floor((endTime - new Date()) / 1000 / 60 / 60)) : 0;
                
                return (
                  <Card 
                    key={game.id}
                    className="gaming-card bg-gradient-to-br from-red-900/30 to-orange-900/20 border-red-500/40 hover:scale-105 transition-all duration-300 relative overflow-hidden"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* LIVE Badge */}
                    <div className="absolute top-4 right-4 z-10">
                      <Badge className="bg-red-500 text-white animate-pulse">
                        <Flame className="w-3 h-3 inline mr-1" />
                        LIVE
                      </Badge>
                    </div>

                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 rounded-lg bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30">
                          <IconComponent className="w-8 h-8 text-red-400" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-xl sm:text-2xl text-white break-words">{game.title}</CardTitle>
                          <p className="text-red-400 text-xs sm:text-sm font-semibold mt-1">Competition Mode</p>
                        </div>
                      </div>
                      
                      {/* Prize Info */}
                      <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Award className="w-5 h-5 text-yellow-400" />
                          <span className="text-yellow-400 font-bold">Prize Pool</span>
                        </div>
                        <p className="text-white text-lg font-semibold">
                          {gameData.prize_description || 'Win Amazing Gadgets!'}
                        </p>
                      </div>

                      <p className="text-gray-400 mb-4">{game.description}</p>
                      
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-black/30 rounded-lg p-3">
                          <Clock className="w-4 h-4 text-red-400 mb-1" />
                          <p className="text-xs text-gray-400">Time Left</p>
                          <p className="text-white font-bold">{timeLeft}h</p>
                        </div>
                        <div className="bg-black/30 rounded-lg p-3">
                          <CircleDollarSign className="w-4 h-4 text-red-400 mb-1" />
                          <p className="text-xs text-gray-400">Entry Fee</p>
                          <p className="text-white font-bold">₹{gameData.entry_fee || 10}</p>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <Button 
                        onClick={() => handlePlayClick(game, true)}
                        className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-4 sm:py-6 text-base sm:text-lg rounded-xl transform transition-all duration-300 hover:scale-105"
                      >
                        <Trophy className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        <span className="text-sm sm:text-base">Pay ₹{gameData.entry_fee || 10} & Compete</span>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Practice Games Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 mb-6">
              <Gamepad2 className="w-4 h-4 inline mr-1" />
              Free to Play
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-4 break-words">
              🎮 Practice Games
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto px-2">
              Play for FREE! Build your skills, track your scores, and have fun
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {loadingGames ? (
              <div className="col-span-2 text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
                <p className="text-gray-400">Loading games...</p>
              </div>
            ) : activeGames.length === 0 ? (
              <div className="col-span-2 text-center py-12">
                <p className="text-2xl text-gray-400 mb-2">No games active right now</p>
                <p className="text-gray-500">Check back soon for new competitions!</p>
              </div>
            ) : practiceGames.length === 0 ? (
              <div className="col-span-2 text-center py-12">
                <Gamepad2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500">No games available at the moment.</p>
              </div>
            ) : (
              games.map((game, index) => {
              const IconComponent = game.icon;
              const colorClasses = {
                blue: 'from-blue-600 to-blue-800 border-blue-500/30',
                purple: 'from-purple-600 to-purple-800 border-purple-500/30',
                yellow: 'from-yellow-600 to-yellow-800 border-yellow-500/30',
                green: 'from-green-600 to-green-800 border-green-500/30'
              };
              
              // Check if this game is in competition mode
              const isCompetitionGame = competitionGames.includes(game.id);
              
              return (
                <Card 
                  key={game.id} 
                  className={`bg-gradient-to-br ${colorClasses[game.color]} border-2 hover:scale-105 transition-all duration-300 animate-fade-in-up ${isCompetitionGame ? 'ring-2 ring-yellow-400 shadow-lg shadow-yellow-400/50' : ''}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30">
                        <IconComponent className="w-8 h-8 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl sm:text-2xl text-white break-words">{game.title}</CardTitle>
                        {isCompetitionGame ? (
                          <Badge className="bg-yellow-500/30 text-yellow-300 border-yellow-500/50 mt-1 text-xs sm:text-sm animate-pulse">
                            <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1 inline" />
                            GAME LIVE • Competition
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 mt-1 text-xs sm:text-sm">
                            Practice Mode • FREE
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-300 mb-4">{game.description}</p>
                    
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-black/30 rounded-lg p-3">
                        <Clock className="w-4 h-4 text-blue-400 mb-1" />
                        <p className="text-xs text-gray-400">Duration</p>
                        <p className="text-white font-bold">{game.duration}s</p>
                      </div>
                      <div className="bg-black/30 rounded-lg p-3">
                        <Target className="w-4 h-4 text-blue-400 mb-1" />
                        <p className="text-xs text-gray-400">Difficulty</p>
                        <p className="text-white font-bold">{game.difficulty}</p>
                      </div>
                    </div>
                    
                    <div className="text-xs text-gray-400 bg-black/20 p-3 rounded">
                      <strong className="text-white">Rules:</strong> {game.rules}
                    </div>
                    
                    {/* Competition Timer */}
                    {isCompetitionGame && competitionTimers[game.id] && (
                      <div className="mt-4">
                        {competitionTimers[game.id].isActive ? (
                          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 text-center">
                            <div className="flex items-center justify-center gap-2 mb-1">
                              <Clock className="w-4 h-4 text-yellow-400" />
                              <span className="text-yellow-400 font-bold text-xs">Competition Ends In:</span>
                            </div>
                            <div className="text-yellow-300 font-black text-2xl">
                              {competitionTimers[game.id].minutes}m {competitionTimers[game.id].seconds}s
                            </div>
                          </div>
                        ) : (
                          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-center">
                            <div className="text-red-400 font-bold text-sm">
                              ⏰ Competition Ended
                            </div>
                            <div className="text-red-300 text-xs mt-1">
                              This game is no longer accepting competition scores
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <Button 
                        onClick={() => {
                          setSelectedGame(game.id);
                          setTimeout(() => {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }, 100);
                        }}
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-bold py-4 sm:py-6 text-base sm:text-lg rounded-xl transform transition-all duration-300 hover:scale-105"
                      >
                        <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        <span className="text-sm sm:text-base">Play for FREE (Practice)</span>
                      </Button>
                      
                      {/* Competition Button - Only show for competition games */}
                      {isCompetitionGame && (
                        <Button 
                          onClick={() => handlePlayClick(game, true)}
                          disabled={competitionTimers[game.id] && !competitionTimers[game.id].isActive}
                          className={`w-full font-bold py-4 sm:py-6 text-base sm:text-lg rounded-xl transform transition-all duration-300 ${
                            competitionTimers[game.id] && !competitionTimers[game.id].isActive
                              ? 'bg-gray-600 cursor-not-allowed opacity-50'
                              : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 hover:scale-105'
                          }`}
                        >
                          <Trophy className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          <span className="text-sm sm:text-base">
                            {competitionTimers[game.id] && !competitionTimers[game.id].isActive
                              ? 'Competition Ended'
                              : 'Pay ₹10 & Compete'
                            }
                          </span>
                        </Button>
                      )}
                      
                      <div className="text-center text-xs text-gray-500 mt-2">
                        {isCompetitionGame 
                          ? '⚡ Competition: 3 attempts • Best score counts'
                          : 'No login required • Unlimited plays'
                        }
                      </div>
                      
                      {/* Hidden old payment logic - keeping for competition mode reference */}
                      {false && (() => {
                        const attempts = playerManager.getGameAttempts(game.id);
                        const hasAttempts = attempts > 0;
                        
                        return (
                          <div className="hidden">
                            <Button 
                              onClick={async () => {
                                // Check if user is logged in
                                const user = JSON.parse(localStorage.getItem('fokatmain_user') || '{}');
                                if (!user.id) {
                                  toast.error('Please login first to play games');
                                  setTimeout(() => {
                                    window.location.href = '/login';
                                  }, 1500);
                                  return;
                                }
                                
                                // Check if user has attempts
                                if (hasAttempts) {
                                  // Start game directly
                                  setSelectedGame(game.id);
                                  // Scroll to top so user can see the game
                                  setTimeout(() => {
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }, 100);
                                } else {
                                  // Check wallet balance first
                                  if (user.wallet_balance >= 10) {
                                    // Deduct from wallet
                                    try {
                                      const token = localStorage.getItem('fokatmain_token');
                                      const response = await fetch(`${window.location.origin}/api/player/wallet/deduct-for-game`, {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${token}`
                                        },
                                        body: JSON.stringify({
                                          game_id: game.id,
                                          game_name: game.title
                                        })
                                      });
                                      
                                      if (response.ok) {
                                        const data = await response.json();
                                        toast.success('₹10 deducted from wallet! Starting game...');
                                        
                                        // Update user data
                                        user.wallet_balance = data.new_balance;
                                        localStorage.setItem('fokatmain_user', JSON.stringify(user));
                                        
                                        // Grant 3 attempts
                                        playerManager.setAttempts(game.id, 3);
                                        
                                        // Start game
                                        setSelectedGame(game.id);
                                        setTimeout(() => {
                                          window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }, 100);
                                      } else {
                                        const error = await response.json();
                                        toast.error(error.detail || 'Failed to deduct from wallet');
                                      }
                                    } catch (error) {
                                      console.error('Wallet deduction error:', error);
                                      toast.error('Failed to process wallet payment');
                                    }
                                  } else {
                                    // Show UPI payment modal
                                    setGameToPlay(game);
                                    setShowPaymentModal(true);
                                  }
                                }
                              }}
                              className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-black font-bold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
                            >
                              {hasAttempts ? (
                                <>
                                  <Play className="w-5 h-5" />
                                  <span>Play Now</span>
                                </>
                              ) : (
                                <>
                                  <span>₹10</span>
                                  <Play className="w-4 h-4" />
                                  <span>Pay & Play</span>
                                </>
                              )}
                            </Button>
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              );
            })
            )}
          </div>
        </div>
      </section>

      {/* How Games Work */}
      <section className="py-20 bg-gradient-to-b from-gray-900/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-white mb-6">
              How Web Games Work
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: 1,
                title: 'Choose & Pay',
                description: 'Select a game and pay ₹10 entry fee via UPI/Paytm',
                icon: Target
              },
              {
                step: 2,
                title: 'Play & Score',
                description: 'Complete the game within time limit, earn points based on performance',
                icon: Trophy
              },
              {
                step: 3,
                title: 'Win Prizes',
                description: 'Top performers win real gadgets, delivered to your doorstep',
                icon: Play
              }
            ].map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="gaming-card text-center p-6">
                  <CardHeader className="pb-4">
                    <div className="mx-auto mb-4 p-4 bg-yellow-400/20 rounded-full w-16 h-16 flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-yellow-400" />
                    </div>
                    <div className="text-3xl font-bold text-yellow-400 mb-2">{step.step}</div>
                    <CardTitle className="text-xl text-white">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Wallet Modal */}
      {showWallet && (
        <PlayerWallet 
          playerId={currentPlayer?.id}
          onClose={() => setShowWallet(false)}
        />
      )}

      {/* UPI Payment Modal */}
      {showPaymentModal && gameToPlay && (
        <UPIPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          gameName={gameToPlay.title}
          gameId={gameToPlay.id}
          amount={10}
          onPaymentSuccess={(paymentData) => {
            // Payment submitted, give user 3 attempts
            console.log('Payment success, giving 3 attempts for:', gameToPlay);
            playerManager.setGameAttempts(gameToPlay.id, 3);
            setShowPaymentModal(false);
            setSelectedGame(gameToPlay.id);
            setGameToPlay(null);
            toast.success('Payment submitted! You have 3 attempts.');
            
            // Scroll to top so user can see the game
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
          }}
        />
      )}

      {/* Welcome Modal for Active Game */}
      {showWelcomeModal && activeGames.length === 1 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 border-2 border-yellow-400 rounded-2xl max-w-md w-full p-8 shadow-2xl animate-scale-in">
            <div className="text-center">
              <div className="mb-6">
                <Trophy className="w-20 h-20 text-yellow-400 mx-auto mb-4 animate-bounce" />
                <h2 className="text-3xl font-bold text-white mb-2">🔥 Live Competition!</h2>
                <p className="text-gray-300 text-lg">
                  {games.find(g => g.id === activeGames[0])?.title || 'Game'} is live right now!
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-6 mb-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Entry Fee:</span>
                  <span className="text-yellow-400 font-bold text-xl">₹10</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Prize:</span>
                  <span className="text-green-400 font-bold">Real Gadgets!</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Duration:</span>
                  <span className="text-white font-semibold">{games.find(g => g.id === activeGames[0])?.duration}s</span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowWelcomeModal(false);
                    const game = games.find(g => g.id === activeGames[0]);
                    if (game) {
                      setGameToPlay(game);
                      setShowPaymentModal(true);
                    }
                  }}
                  className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-black font-bold py-4 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
                >
                  <Zap className="w-5 h-5" />
                  <span>Play Now & Win!</span>
                </button>
                <button
                  onClick={() => setShowWelcomeModal(false)}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-xl transition-colors"
                >
                  Browse All Games
                </button>
              </div>

              <p className="text-gray-500 text-xs mt-4">
                💡 Tip: Higher scores = Better chances to win gadgets!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayGames;